(function () {
  if (customElements.get("bench-related-products")) return;

  class BenchRelatedProducts extends HTMLElement {
    static productSearchCache = new Map();
    static productPageCache = new Map();
    static productPagePrefetches = new Map();

    connectedCallback() {
      if (this.initialized) return;

      this.results = this.querySelector("[data-results]");
      this.carousel = this.querySelector("[data-carousel]");
      this.controls = this.querySelector("[data-controls]");
      if (!this.results || !this.carousel) {
        requestAnimationFrame(() => this.connectedCallback());
        return;
      }

      this.initialized = true;
      this.productByItem = new WeakMap();
      this.autoScrollTimer = null;
      this.isSwappingProduct = false;

      this.bindEvents();
      this.load();
    }

    bindEvents() {
      this.results.addEventListener("click", (event) => this.handleItemClick(event), true);
      this.results.addEventListener("mouseenter", () => this.stopAutoScroll());
      this.results.addEventListener("mouseleave", () => this.startAutoScroll());
      this.querySelector("[data-scroll-prev]")?.addEventListener("click", () => this.scrollByStep(-1));
      this.querySelector("[data-scroll-next]")?.addEventListener("click", () => this.scrollByStep(1));
    }

    get currentSku() {
      return (this.dataset.currentSku || "").trim();
    }

    get currentTitle() {
      return this.dataset.currentTitle || "";
    }

    get maxProducts() {
      return Number(this.dataset.maxProducts || 24);
    }

    get hideSingle() {
      return this.dataset.hideSingle === "true";
    }

    get autoplayEnabled() {
      return this.dataset.autoplay === "true";
    }

    get autoplayInterval() {
      return Math.max(Number(this.dataset.autoplayInterval || 3000), 1000);
    }

    getMatchConfig() {
      const parts = this.currentSku.split("-").filter(Boolean);
      const titleHasBench = /bench/i.test(this.currentTitle);

      if (titleHasBench && parts.length === 3 && /^\d{3,4}$/.test(parts[1])) {
        return {
          mode: "bench",
          key: parts.slice(0, 2).join("-"),
          query: parts.slice(0, 2).join("-")
        };
      }

      if (parts.length >= 6 && /^\d{3}$/.test(parts[parts.length - 2])) {
        return {
          mode: "hat",
          key: parts.slice(-3).join("-"),
          query: parts.slice(-3).join("-")
        };
      }

      return null;
    }

    async load() {
      const config = this.getMatchConfig();
      if (!config) {
        this.hidden = true;
        return;
      }

      const products = await this.fetchProducts(config.query);
      const matches = this.filterMatches(products, config).slice(0, this.maxProducts);

      if (matches.length <= 1 && this.hideSingle) {
        this.hidden = true;
        return;
      }

      if (!matches.length) {
        this.hidden = true;
        return;
      }

      this.render(matches);
    }

    async fetchProducts(query) {
      if (BenchRelatedProducts.productSearchCache.has(query)) {
        return BenchRelatedProducts.productSearchCache.get(query);
      }

      const root = window.Shopify?.routes?.root || "/";
      const queries = [`variants.sku:${query}*`, query];
      const merged = new Map();

      for (const searchQuery of queries) {
        const params = new URLSearchParams();
        params.set("view", "bench-related");
        params.set("type", "product");
        params.set("q", searchQuery);
        params.set("options[prefix]", "last");
        params.set("options[unavailable_products]", "last");

        try {
          const response = await fetch(`${root}search?${params.toString()}`);
          if (!response.ok) continue;
          const data = await response.json();
          (data.products || []).forEach((product) => merged.set(product.handle, product));
        } catch (error) {
          // Keep the block silent if storefront search is unavailable.
        }
      }

      const products = Array.from(merged.values());
      BenchRelatedProducts.productSearchCache.set(query, products);
      return products;
    }

    filterMatches(products, config) {
      const matches = [];

      products.forEach((product) => {
        const variant = (product.variants || []).find((item) => {
          const sku = (item.sku || "").trim();
          if (config.mode === "bench") {
            const parts = sku.split("-").filter(Boolean);
            return /bench/i.test(product.title || "") && parts.length === 3 && parts.slice(0, 2).join("-") === config.key;
          }

          const parts = sku.split("-").filter(Boolean);
          return parts.length >= 6 && parts.slice(-3).join("-") === config.key;
        });

        if (!variant) return;

        matches.push({
          ...product,
          matchVariant: variant,
          isActive: product.handle === this.dataset.currentHandle || String(variant.id) === this.dataset.currentVariantId
        });
      });

      return matches.sort((a, b) => Number(b.isActive) - Number(a.isActive) || a.title.localeCompare(b.title, "tr"));
    }

    render(products) {
      this.results.innerHTML = "";

      products.forEach((product) => {
        const link = document.createElement("a");
        link.className = `bench-related-products__item${product.isActive ? " is-active" : ""}`;
        link.href = this.getProductUrl(product);
        link.title = product.title;
        link.setAttribute("aria-label", product.title);

        if (product.image || product.matchVariant.image) {
          const image = document.createElement("img");
          image.src = product.matchVariant.image || product.image;
          image.alt = product.title;
          image.loading = "lazy";
          link.appendChild(image);
        } else {
          link.appendChild(document.createElement("span")).className = "bench-related-products__placeholder";
        }

        this.productByItem.set(link, product);
        this.results.appendChild(link);
      });

      this.carousel.hidden = false;
      this.hidden = false;
      requestAnimationFrame(() => {
        this.updateCarouselState();
        this.startAutoScroll();
        this.prefetchProductPages(products);
      });
    }

    handleItemClick(event) {
      const item = event.target.closest(".bench-related-products__item");
      if (!item) return;

      const product = this.productByItem.get(item);
      if (!product) return;

      event.preventDefault();
      this.swapProduct(product, item.href);
    }

    getProductUrl(product) {
      const url = product.url || `/products/${product.handle}`;
      const variantId = product.matchVariant?.id;
      if (!variantId) return url;

      try {
        const parsedUrl = new URL(url, window.location.origin);
        parsedUrl.searchParams.set("variant", variantId);
        return parsedUrl.pathname + parsedUrl.search + parsedUrl.hash;
      } catch (error) {
        const separator = url.includes("?") ? "&" : "?";
        return `${url}${separator}variant=${encodeURIComponent(variantId)}`;
      }
    }

    async swapProduct(product, url) {
      if (this.isSwappingProduct) return;

      const currentUrl = new URL(window.location.href);
      const nextUrl = new URL(url, window.location.origin);
      if (currentUrl.pathname === nextUrl.pathname && currentUrl.search === nextUrl.search) return;

      const oldMain = document.querySelector("main");
      if (!oldMain) {
        window.location.href = url;
        return;
      }

      this.isSwappingProduct = true;
      this.classList.add("is-loading");
      this.stopAutoScroll();

      try {
        const html = await this.getProductPage(nextUrl.href);
        const newMain = html.querySelector("main");
        if (!newMain) throw new Error("Product page markup is missing");
        this.prepareSwappedMain(newMain);

        const preProcessCallbacks = [
          (node) => node.querySelectorAll(".scroll-trigger").forEach((element) => {
            element.classList.add("scroll-trigger--cancel");
          })
        ];
        const postProcessCallbacks = [
          (node) => {
            // AJAX insertion connects the gallery before its thumbnail sibling.
            // Initialize thumbnails first so Swiper can bind both instances.
            node.querySelectorAll("swiper-product-thumbs").forEach((element) => element.init?.());
            node.querySelectorAll("swiper-product-gallery").forEach((element) => element.init?.());
          },
          () => {
            window?.Shopify?.PaymentButton?.init?.();
            window?.ProductModel?.loadShopifyXR?.();
          }
        ];

        document.querySelector("head title").innerHTML = html.querySelector("head title")?.innerHTML || document.title;

        const htmlUpdateUtility = typeof HTMLUpdateUtility !== "undefined" ? HTMLUpdateUtility : window.HTMLUpdateUtility;
        if (htmlUpdateUtility?.viewTransition) {
          htmlUpdateUtility.viewTransition(oldMain, newMain, preProcessCallbacks, postProcessCallbacks);
        } else {
          preProcessCallbacks.forEach((callback) => callback(newMain));
          oldMain.replaceWith(newMain);
          postProcessCallbacks.forEach((callback) => callback(newMain));
        }

        window.history.pushState({ path: nextUrl.href }, "", nextUrl.href);
        window.scrollTo({ top: 0, behavior: "smooth" });
      } catch (error) {
        window.location.href = url;
      } finally {
        this.isSwappingProduct = false;
        this.classList.remove("is-loading");
      }
    }

    prepareSwappedMain(newMain) {
      // Keep inert product data and Swiper options; only executable scripts must
      // be removed before HTMLUpdateUtility reinserts the new product markup.
      newMain.querySelectorAll("script").forEach((script) => {
        const type = (script.getAttribute("type") || "").trim().toLowerCase();
        if (script.hasAttribute("src") || !["application/json", "application/ld+json"].includes(type)) {
          script.remove();
        }
      });
      newMain.querySelectorAll(".js-animation-fade-in").forEach((element) => {
        element.classList.remove("js-animation-fade-in", "animation-init");
        element.classList.add("animation-none");
        element.style.opacity = "";
        element.style.transform = "";
      });
      newMain.querySelectorAll(".scroll-trigger").forEach((element) => {
        element.classList.add("scroll-trigger--cancel");
      });
    }

    async getProductPage(url) {
      if (BenchRelatedProducts.productPageCache.has(url)) {
        return BenchRelatedProducts.productPageCache.get(url).cloneNode(true);
      }

      if (BenchRelatedProducts.productPagePrefetches.has(url)) {
        const cachedHtml = await BenchRelatedProducts.productPagePrefetches.get(url);
        if (cachedHtml) return cachedHtml.cloneNode(true);
      }

      const html = await this.fetchProductPage(url);
      BenchRelatedProducts.productPageCache.set(url, html);
      return html.cloneNode(true);
    }

    async fetchProductPage(url) {
      const response = await fetch(url, {
        headers: { "X-Requested-With": "XMLHttpRequest" }
      });
      if (!response.ok) throw new Error("Product page could not be loaded");
      return new DOMParser().parseFromString(await response.text(), "text/html");
    }

    prefetchProductPages(products) {
      const idleCallback = window.requestIdleCallback || ((callback) => window.setTimeout(callback, 500));

      idleCallback(() => {
        products.forEach((product) => {
          const url = new URL(this.getProductUrl(product), window.location.origin).href;
          if (BenchRelatedProducts.productPageCache.has(url) || BenchRelatedProducts.productPagePrefetches.has(url)) return;

          const prefetch = this.fetchProductPage(url)
            .then((html) => {
              BenchRelatedProducts.productPageCache.set(url, html);
              BenchRelatedProducts.productPagePrefetches.delete(url);
              return html;
            })
            .catch((error) => {
              BenchRelatedProducts.productPagePrefetches.delete(url);
              return null;
            });

          BenchRelatedProducts.productPagePrefetches.set(url, prefetch);
        });
      });
    }

    updateCarouselState() {
      const isDraggable = this.results.scrollWidth > this.results.clientWidth + 2;
      this.results.classList.toggle("is-draggable", isDraggable);
      if (this.controls) this.controls.hidden = !isDraggable;
    }

    getStepSize() {
      const firstItem = this.results.querySelector(".bench-related-products__item");
      if (!firstItem) return 120;

      const styles = window.getComputedStyle(this.results);
      const gap = parseFloat(styles.columnGap || styles.gap || 0) || 0;
      return firstItem.offsetWidth + gap;
    }

    scrollByStep(direction) {
      this.stopAutoScroll();
      this.results.scrollBy({
        left: this.getStepSize() * direction,
        behavior: "smooth"
      });
      this.startAutoScroll();
    }

    startAutoScroll() {
      this.stopAutoScroll();
      if (!this.autoplayEnabled) return;
      if (this.results.scrollWidth <= this.results.clientWidth + 2) return;

      this.autoScrollTimer = window.setInterval(() => {
        const step = this.getStepSize();
        const maxScroll = this.results.scrollWidth - this.results.clientWidth;
        if (this.results.scrollLeft >= maxScroll - 2) {
          this.results.scrollTo({ left: 0, behavior: "smooth" });
        } else {
          this.results.scrollBy({ left: step, behavior: "smooth" });
        }
      }, 3000);
    }

    stopAutoScroll() {
      if (!this.autoScrollTimer) return;
      window.clearInterval(this.autoScrollTimer);
      this.autoScrollTimer = null;
    }

  }

  customElements.define("bench-related-products", BenchRelatedProducts);
})();
